import { z } from 'zod/v4';

import { RiskManagementNotificationFrequencyRuntype } from './riskNotificationsBackendTypes';

export enum RiskDigestSeverity {
    Critical = 'critical',
    Warning = 'warning',
    Review = 'review',
    Info = 'info',
}
export const RiskDigestSeverityRuntype = z.enum(RiskDigestSeverity);

export type RiskDigestRecommendedAction = z.infer<typeof RiskDigestRecommendedActionRuntype>;
export const RiskDigestRecommendedActionRuntype = z.enum([
    'investigate_eol',
    'review_compliance',
    'review_process_change',
    'documentation_update',
    'no_action',
    'informational',
]);

export type RiskDigestClassification = z.infer<typeof RiskDigestClassificationRuntype>;
export const RiskDigestClassificationRuntype = z.object({
    severity: RiskDigestSeverityRuntype,
    recommended_action: RiskDigestRecommendedActionRuntype,
    is_lifecycle: z.boolean(),
});

export type RiskDigestAffectedAssembly = z.infer<typeof RiskDigestAffectedAssemblyRuntype>;
export const RiskDigestAffectedAssemblyRuntype = z.object({
    id: z.string(),
    name: z.string(),
});

export type RiskDigestAlert = z.infer<typeof RiskDigestAlertRuntype>;
export const RiskDigestAlertRuntype = z.object({
    classification: RiskDigestClassificationRuntype,
    pcn_number: z.string(),
    mpn: z.string(),
    manufacturer: z.string(),
    type_of_change: z.string(),
    description: z.string(),
    source_url: z.string(),
    /** Date the manufacturer issued the notice — a PCN's publication date, not its effective date. */
    alert_date: z.iso.date().nullable(),
    /** When Luminovo first ingested the alert — this is what the digest window filters on. */
    first_seen_at: z.iso.datetime(),
    /** When Luminovo last re-saw the alert; tracks our own activity, not the alert's. */
    last_seen_at: z.iso.datetime(),
    affected_assemblies: z.array(RiskDigestAffectedAssemblyRuntype),
    in_library: z.boolean(),
});

export type RiskDigestStats = z.infer<typeof RiskDigestStatsRuntype>;
export const RiskDigestStatsRuntype = z.object({
    changes_detected: z.number(),
    critical: z.number(),
    warning: z.number(),
    review: z.number(),
    auto_resolved_count: z.number(),
});

export type RiskDigestResponse = z.infer<typeof RiskDigestResponseRuntype>;
export const RiskDigestResponseRuntype = z.object({
    stats: RiskDigestStatsRuntype,
    changes: z.array(RiskDigestAlertRuntype),
    auto_resolved: z.array(RiskDigestAlertRuntype),
});

export type TopAffectedAssemblyItem = z.infer<typeof TopAffectedAssemblyItemRuntype>;
export const TopAffectedAssemblyItemRuntype = z.object({
    assembly_id: z.string(),
    assembly_designator: z.string(),
    distinct_alerts: z.number(),
    distinct_affected_mpns: z.number(),
    bom_item_count: z.number(),
});

export type TopAffectedAssemblyResponse = z.infer<typeof TopAffectedAssemblyResponseRuntype>;
export const TopAffectedAssemblyResponseRuntype = z.object({
    items: z.array(TopAffectedAssemblyItemRuntype),
});

export type TimelineFilterQueryParams = z.infer<typeof TimelineFilterQueryParamsRuntype>;
export const TimelineFilterQueryParamsRuntype = z.discriminatedUnion('query_type', [
    z.object({
        query_type: z.literal('frequency'),
        frequency: RiskManagementNotificationFrequencyRuntype,
    }),
    z.object({
        query_type: z.literal('time_period'),
        start: z.iso.datetime(),
        end: z.iso.datetime(),
    }),
]);

export enum RiskDigestGeoGrade {
    Poor = 'poor',
    Fair = 'fair',
    Good = 'good',
    Unknown = 'unknown',
}
export const RiskDigestGeoGradeRuntype = z.enum(RiskDigestGeoGrade);

export enum RiskDigestDisruptionImpact {
    Impacted = 'impacted',
    PossibleImpacted = 'possible_impacted',
    None = 'none',
}
export const RiskDigestDisruptionImpactRuntype = z.enum(RiskDigestDisruptionImpact);

/** Whether SiliconExpert sees any manufacturing step concentrated at one site. */
export enum RiskDigestSourcingFootprint {
    SingleSite = 'single_site',
    MultipleSites = 'multiple_sites',
    /** SiliconExpert is quota-metered, so the part may have no geo-risk snapshot. */
    Unknown = 'unknown',
}
export const RiskDigestSourcingFootprintRuntype = z.enum(RiskDigestSourcingFootprint);

export type RiskDigestGeoRiskEntry = z.infer<typeof RiskDigestGeoRiskEntryRuntype>;
export const RiskDigestGeoRiskEntryRuntype = z.object({
    severity: RiskDigestSeverityRuntype,
    mpn: z.string(),
    manufacturer: z.string(),
    geo_grade: RiskDigestGeoGradeRuntype,
    countries: z.array(z.string()),
    sourcing_footprint: RiskDigestSourcingFootprintRuntype,
    disruption_impact: RiskDigestDisruptionImpactRuntype,
    facilities_impacted: z.string(),
    affected_assemblies: z.array(RiskDigestAffectedAssemblyRuntype),
    in_library: z.boolean(),
    /**
     * Most recent geo-risk event for the part inside the requested window.
     * Never null. A part with no event in the window is not in the snapshot.
     */
    latest_event_date: z.iso.datetime(),
});

export type RiskDigestGeoRiskStats = z.infer<typeof RiskDigestGeoRiskStatsRuntype>;
export const RiskDigestGeoRiskStatsRuntype = z.object({
    /** Equal to `entries.length`. The backend counts exactly what it returns. */
    parts_scored: z.number(),
    geo_poor: z.number(),
    geo_fair: z.number(),
    geo_good: z.number(),
    actively_impacted: z.number(),
});

export type RiskDigestGeoRiskResponse = z.infer<typeof RiskDigestGeoRiskResponseRuntype>;
export const RiskDigestGeoRiskResponseRuntype = z.object({
    stats: RiskDigestGeoRiskStatsRuntype,
    entries: z.array(RiskDigestGeoRiskEntryRuntype),
});
